import React, { createContext, useCallback, useMemo, useState } from 'react';
import { tokenStorage, decodeJwt } from '@/utils/jwt';
import { AuthUser, authService } from '@/services/auth.service';

interface AuthCtx {
  user: AuthUser | null;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthCtx | null>(null);

/**
 * Restore the session synchronously. Doing it in an effect left `user` null on
 * the first render, so ProtectedRoute sent every page refresh to /login.
 */
function rehydrateUser(): AuthUser | null {
  const t = tokenStorage.get();
  if (!t) return null;
  const p = decodeJwt(t);
  if (!p || p.exp * 1000 < Date.now()) { tokenStorage.clear(); return null; }
  return { userUid: p.sub, role: p.role, companyUid: p.companyUid };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(rehydrateUser);

  const onAuthSuccess = useCallback((token: string, u: AuthUser) => {
    tokenStorage.set(token); setUser(u);
  }, []);

  const value = useMemo<AuthCtx>(() => ({
    user,
    loginWithPassword: async (email, password) => {
      const r = await authService.loginWithPassword(email, password);
      onAuthSuccess(r.token, r.user);
    },
    logout: () => { tokenStorage.clear(); setUser(null); },
  }), [user, onAuthSuccess]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
